/**
 * The two recordings, side by side, on one clock.
 *
 * Each track is a list of ~10 s chunks with an `offset_ms` measured from
 * `attempts.started_at`. These chunks are NOT independently playable: the
 * recorder writes them with a single `MediaRecorder.start(timeslice)` call, so
 * only chunk 0 carries the WebM header and every later chunk is a headerless
 * fragment (see `lib/recorder/recorder.ts` and docs/API.md §5). Handing each
 * chunk to a `<video>` in turn therefore plays chunk 0 and then stalls forever
 * on the first fragment. Instead, each contiguous run of chunks is fetched and
 * **concatenated back into one Blob** — reuniting the header with its fragments
 * — which is a complete, seekable WebM the browser plays end to end.
 *
 * The clock in `useSyncedPlayback` is the authority. Each track's `<video>`
 * continuously corrects itself toward it, so the two views stay together, a
 * seek lands in both, and a device that recorded nothing for a stretch shows a
 * "no signal" block for exactly that stretch instead of quietly skipping ahead.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Camera,
  Loader2,
  MonitorPlay,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  SkipBack,
  Smartphone,
  VideoOff,
  Volume2,
  VolumeX,
} from "lucide-react";
import type { RecordingSource } from "@exam/shared";
import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatClock, formatOffset } from "../lib/format";
import type { Track, TrackSegment } from "../data/types";
import { audibleSource, audioCandidates, isMutedFor } from "./audioChoice";
import { RunLoader } from "./runLoader";
import type { PlaybackClock } from "./useSyncedPlayback";

/** Past this much disagreement with the master clock, the video re-seeks. */
const DRIFT_TOLERANCE_S = 0.35;

const SOURCE_LABEL: Record<RecordingSource, string> = {
  webcam: "Laptop webcam",
  phone: "Phone side camera",
  screen: "Screen recording",
};
const SOURCE_ICON: Record<RecordingSource, typeof Camera> = {
  webcam: Camera,
  phone: Smartphone,
  screen: MonitorPlay,
};

/** "5.5 min" / "48 s" — a rough length, for a one-line note. */
function formatDurationMin(ms: number): string {
  return ms < 60_000
    ? `${Math.round(ms / 1000)} s`
    : `${(ms / 60_000).toFixed(1)} min`;
}

/** The day that footage was recorded, for telling two occasions apart. */
function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
}

/** Index of the contiguous run that covers `t`, or -1 for a hole. */
function runCovering(runs: TrackSegment[], t: number): number {
  for (let i = 0; i < runs.length; i++) {
    const r = runs[i]!;
    if (t >= r.start_ms && t < r.end_ms) return i;
  }
  return -1;
}

/* ------------------------------------------------------------------ */
/* Sound                                                               */
/* ------------------------------------------------------------------ */

interface AudioChoice {
  /** The one track that plays its sound. Null when nothing was recorded. */
  source: RecordingSource | null;
  setSource: (source: RecordingSource) => void;
  muted: boolean;
  setMuted: (muted: boolean) => void;
  /** 0-1, applied to whichever track is the source. */
  volume: number;
  setVolume: (volume: number) => void;
  /** The tracks a reviewer can choose between — those that have footage. */
  candidates: RecordingSource[];
  /** Whether this track's element should be silent. */
  mutedFor: (source: RecordingSource) => boolean;
}

/**
 * Which recording a reviewer hears, and how loudly.
 *
 * The rule itself lives in `audioChoice.ts`; this is the state around it.
 *
 * It is a CHOICE rather than a constant because the default can be the wrong
 * guess — a webcam that recorded silence while the phone beside the desk heard
 * the room — and a reviewer who cannot switch has no way to find that out.
 */
function useAudioChoice(tracks: Track[]): AudioChoice {
  const candidates = useMemo(() => audioCandidates(tracks), [tracks]);
  const [chosen, setChosen] = useState<RecordingSource | null>(null);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  const source = audibleSource(candidates, chosen);

  return {
    source,
    setSource: setChosen,
    muted,
    setMuted,
    volume,
    setVolume,
    candidates,
    mutedFor: (s) => isMutedFor(s, source, muted),
  };
}

/* ------------------------------------------------------------------ */
/* One track                                                           */
/* ------------------------------------------------------------------ */

function TrackView({
  track,
  clock,
  muted,
  volume = 1,
  compact = false,
  fill = false,
}: {
  track: Track;
  clock: PlaybackClock;
  muted: boolean;
  /** 0-1. Only the unmuted track is audible, so this is the review volume. */
  volume?: number;
  /**
   * In the narrow right-hand column. A 16:9 pane there would be a letterbox
   * strip too short to read a face in, so the cameras get a 4:3 pane — which is
   * closer to what a webcam actually produces anyway.
   */
  compact?: boolean;
  /**
   * Take whatever height the parent gives instead of a fixed aspect ratio.
   * Used by review mode, where the screen recording is meant to fill the
   * window rather than sit in a card.
   */
  fill?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { timeMs, playing, rate } = clock;

  /**
   * The playable runs: each contiguous span of chunks from one recorder
   * session. A gap (device dropout, or the webcam not yet started during setup)
   * splits runs, because a fresh recorder session begins its own WebM header.
   */
  const runs = useMemo(
    () => track.segments.filter((s) => s.kind === "chunks"),
    [track.segments],
  );

  /**
   * One concatenated, seekable object URL per run — built by fetching every
   * chunk of the run and joining the bytes. `null` while a run has not been
   * built; a run that failed to fetch is recorded in `failedRuns`.
   *
   * **Built ON DEMAND, not all at once.** Every run of every track used to be
   * fetched the moment the screen mounted, and nothing appeared until the run
   * under the playhead had finished downloading — so opening a 23-minute
   * attempt pulled ~55 MB (three devices, 295 chunks) before the first frame,
   * and on a slow connection the page simply said "Loading footage…" for
   * minutes. Most of those bytes were never watched.
   *
   * Now only the run being watched is fetched, plus the one after it so
   * playback does not stall at a run boundary. Scrubbing into an unbuilt run
   * builds it then. Everything already built stays built — `loadedRef` is what
   * makes a second visit to a run instant, and what stops two renders from
   * fetching the same run twice.
   */
  const [runUrls, setRunUrls] = useState<(string | null)[]>([]);
  const [failedRuns, setFailedRuns] = useState<Record<number, string>>({});
  const objectUrlsRef = useRef<string[]>([]);
  /**
   * Which runs are built or in flight, and which manifest they belong to.
   * See `runLoader.ts` — the rules there are subtle and are tested on their own.
   */
  const loaderRef = useRef(new RunLoader());
  /**
   * Runs the browser refused to decode.
   *
   * The backstop to `decodable`, which predicts the known cause — missing
   * opening segments — from the chunk indices. Anything it does not predict
   * (truncated bytes, a codec the reviewer's browser lacks) still arrives as
   * an `error` event on the element, and without this it arrives nowhere: the
   * pane just stays white and says nothing.
   */
  const [undecodableRuns, setUndecodableRuns] = useState<Record<number, true>>(
    {},
  );
  // A new manifest (or expired URLs) throws everything away and starts over.
  useEffect(() => {
    loaderRef.current.reset();
    for (const u of objectUrlsRef.current) URL.revokeObjectURL(u);
    objectUrlsRef.current = [];
    setRunUrls(new Array(runs.length).fill(null));
    setFailedRuns({});
    setUndecodableRuns({});
  }, [runs]);

  // Revoke everything on unmount so the blobs are not leaked.
  useEffect(
    () => () => {
      for (const u of objectUrlsRef.current) URL.revokeObjectURL(u);
      objectUrlsRef.current = [];
    },
    [],
  );

  const runIndex = runCovering(runs, timeMs);

  // Build the run being watched, then the one after it. Re-entered whenever the
  // playhead crosses into a different run.
  useEffect(() => {
    if (runIndex < 0) return;
    const loader = loaderRef.current;
    const gen = loader.generation;

    const build = async (i: number) => {
      if (i < 0 || i >= runs.length) return;
      // Nothing can read these bytes, so do not spend a reviewer's connection
      // fetching them — a broken run can be tens of megabytes.
      if (!runs[i]!.decodable) return;

      // A retry starts clean: leaving the old message set would keep the pane
      // reading "Could not load" through the attempt that is fixing it.
      setFailedRuns((prev) => {
        if (prev[i] === undefined) return prev;
        const next = { ...prev };
        delete next[i];
        return next;
      });

      try {
        const merged = await loader.build(gen, i, async () => {
          const parts = await Promise.all(
            runs[i]!.chunks.map(async (chunk) => {
              // Same-origin signed URL (`/api/media/playback?…`), so a plain
              // fetch reads the bytes without any CORS dance.
              const res = await fetch(chunk.url);
              if (!res.ok) {
                throw new Error(
                  `chunk ${chunk.chunk_index}: HTTP ${res.status}`,
                );
              }
              return res.blob();
            }),
          );
          return new Blob(parts, { type: parts[0]?.type || "video/webm" });
        });
        // `null` means this build produced nothing to apply — another render
        // already owns the run, or the manifest moved on underneath it.
        if (merged === null) return;

        const objectUrl = URL.createObjectURL(merged);
        objectUrlsRef.current.push(objectUrl);
        setRunUrls((prev) => {
          const next = prev.slice();
          next[i] = objectUrl;
          return next;
        });
      } catch (err) {
        setFailedRuns((prev) => ({
          ...prev,
          [i]: err instanceof Error ? err.message : "could not load",
        }));
      }
    };

    void build(runIndex).then(() => build(runIndex + 1));
  }, [runs, runIndex]);

  const run = runIndex >= 0 ? runs[runIndex]! : null;
  const url = runIndex >= 0 ? (runUrls[runIndex] ?? null) : null;
  const failed = runIndex >= 0 ? failedRuns[runIndex] : undefined;
  /**
   * Footage that exists on the server and cannot be watched — either predicted
   * from a hole in the chunk indices, or reported by the element itself.
   */
  const broken =
    run !== null && (!run.decodable || undecodableRuns[runIndex] === true);
  const loading =
    run !== null && !broken && url === null && failed === undefined;

  const signal: "ok" | "loading" | "gap" | "broken" = broken
    ? "broken"
    : run && url
      ? "ok"
      : loading
        ? "loading"
        : "gap";

  // Drive the single <video> off the master clock. Setting `currentTime` before
  // metadata is in gets dropped by the browser, so the first seek of a newly
  // loaded run is deferred to `loadedmetadata`.
  const loadedUrlRef = useRef<string | null>(null);
  const wantedRef = useRef(0);
  const playingRef = useRef(playing);
  const rateRef = useRef(rate);
  playingRef.current = playing;
  rateRef.current = rate;

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (!run || !url) {
      if (!el.paused) el.pause();
      return;
    }

    const wanted = Math.max(0, (timeMs - run.start_ms) / 1000);
    wantedRef.current = wanted;

    if (loadedUrlRef.current !== url) {
      loadedUrlRef.current = url;
      el.src = url;
      el.addEventListener(
        "loadedmetadata",
        () => {
          el.currentTime = wantedRef.current;
          el.playbackRate = rateRef.current;
          if (playingRef.current) void el.play().catch(() => undefined);
        },
        { once: true },
      );
      return;
    }

    // Metadata not in yet — the loadedmetadata handler owns the first seek.
    if (el.readyState < 1) return;

    if (Math.abs(el.currentTime - wanted) > DRIFT_TOLERANCE_S) {
      el.currentTime = wanted;
    }
    el.playbackRate = rate;
    if (playing) {
      // A play() rejection is normal (autoplay policy, or a seek racing the
      // promise); the next tick tries again.
      void el.play().catch(() => undefined);
    } else if (!el.paused) {
      el.pause();
    }
    // `timeMs` intentionally drives this effect — it is the sync mechanism.
  }, [run, url, timeMs, playing, rate]);

  // `volume` is a property, not an attribute — React will not set it from
  // JSX, and a run that loads later starts at the element default unless it is
  // re-applied per source.
  useEffect(() => {
    const el = videoRef.current;
    if (el) el.volume = volume;
  }, [volume, url]);

  const Icon = SOURCE_ICON[track.source];
  const hasChunks = track.chunks.length > 0;
  /**
   * Footage exists but cannot be decoded, because chunk 0 — the only one
   * carrying the WebM header — never reached the server. It plays as a black
   * pane, which is indistinguishable from a camera that recorded nothing, so
   * it is called out explicitly. Silence here is how a reviewer concludes
   * "the phone was off" about an attempt that has 8 MB of phone video.
   */
  const headerMissing = hasChunks && track.header_missing;
  /** This device has footage, but none yet at the position being watched. */
  const notYetRolling = hasChunks && timeMs < track.first_offset_ms;
  /**
   * The far left of the timeline belongs to the phone alone — it records from
   * pairing, through the paper check and room scan, while the laptop webcam
   * only starts when the exam does. That is expected, so it gets its own
   * sentence rather than reading as a dropout.
   */
  const beforeExamStart = notYetRolling && timeMs < 0;

  return (
    <div className={cn("min-w-0", fill && "flex min-h-0 flex-1 flex-col")}>
      {/* `shrink-0` so the label row keeps its height when the pane below is
          flexed to fill — otherwise the flex container steals it back. */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-1.5">
        <span className="flex items-center gap-1.5 text-xs font-medium text-ink">
          <Icon className="size-3.5 text-ink-faint" />
          {SOURCE_LABEL[track.source]}
        </span>
        <span
          className={cn(
            "text-2xs",
            headerMissing ? "font-medium text-violation" : "text-ink-faint",
          )}
        >
          {headerMissing
            ? "Unplayable"
            : !hasChunks
              ? "No recording"
              : signal === "broken"
                ? "Unplayable"
                : signal === "loading"
                  ? "Loading…"
                  : signal === "gap"
                    ? beforeExamStart
                      ? "Not started"
                      : "No signal"
                    : formatOffset(timeMs)}
        </span>
      </div>

      <div
        className={cn(
          "relative w-full bg-surface-sunken",
          fill ? "min-h-0 flex-1" : compact ? "aspect-[4/3]" : "aspect-video",
        )}
      >
        <video
          ref={videoRef}
          muted={muted}
          playsInline
          preload="auto"
          // The element is the only thing that knows the bytes were unreadable.
          // Unlistened-to, that knowledge stayed in the DOM and the reviewer
          // got a white rectangle.
          onError={() => {
            if (runIndex >= 0) {
              setUndecodableRuns((prev) => ({ ...prev, [runIndex]: true }));
            }
          }}
          className={cn(
            "absolute inset-0 size-full object-contain",
            signal === "ok" ? "opacity-100" : "opacity-0",
          )}
        />

        {headerMissing ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 border border-violation/40 bg-violation-soft px-4 text-center">
            <VideoOff className="size-4 text-violation" />
            <p className="text-xs font-medium text-violation">
              Footage uploaded, but unplayable
            </p>
            <p className="text-2xs text-ink-muted">
              {track.chunks.length} segments reached the server, but the first
              one — the only one carrying the video header — did not, so none of
              it can be decoded. The recording exists; it cannot be watched.
            </p>
          </div>
        ) : signal === "broken" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 border border-violation/40 bg-violation-soft px-4 text-center">
            <VideoOff className="size-4 text-violation" />
            <p className="text-xs font-medium text-violation">
              This stretch cannot be played
            </p>
            <p className="text-2xs text-ink-muted">
              {run
                ? `${formatOffset(run.start_ms)} to ${formatOffset(run.end_ms)} reached the server, but the segments that begin it did not — so these bytes cannot be decoded. The rest of the recording is unaffected.`
                : "These bytes could not be decoded."}
            </p>
          </div>
        ) : signal === "loading" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 border border-border bg-surface-sunken">
            <Loader2 className="size-4 animate-spin text-ink-faint" />
            <p className="text-2xs text-ink-faint">Loading footage…</p>
          </div>
        ) : signal === "gap" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 border border-border bg-surface-sunken">
            <VideoOff className="size-4 text-ink-faint" />
            <p className="text-xs font-medium text-ink-muted">
              {failed
                ? "Could not load"
                : beforeExamStart
                  ? "Not started yet"
                  : "No signal"}
            </p>
            <p className="px-4 text-center text-2xs text-ink-faint">
              {failed
                ? "This segment's video could not be fetched — the link may have expired."
                : !hasChunks
                  ? "This device uploaded nothing during this sitting."
                  : beforeExamStart
                    ? `The exam had not started. This camera begins recording at ${formatOffset(track.first_offset_ms)}.`
                    : notYetRolling
                      ? `This camera joined at ${formatOffset(track.first_offset_ms)}.`
                      : "This device stopped uploading here."}
            </p>
          </div>
        ) : null}
      </div>

      {/* Footage that exists but is not on this timeline.
          BELOW the pane, not over it: it is needed exactly when the pane is
          showing "No recording", and an overlay covers the sentence that
          explains what that means. A device holding megabytes of video reading
          as though it recorded nothing is the one conclusion this screen must
          never lead a reviewer to. */}
      {track.earlier ? (
        <p className="shrink-0 border-t border-warn/30 bg-warn-soft px-3 py-1.5 text-2xs text-warn">
          Also {formatDurationMin(track.earlier.duration_ms)} from{" "}
          {formatDate(track.earlier.ended_at_ms)} — a separate occasion against
          this same attempt, so it is not on this timeline.
        </p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Both tracks                                                         */
/* ------------------------------------------------------------------ */

const RATES = [0.5, 1, 1.5, 2, 4];

export function DualPlayer({
  tracks,
  clock,
  aside,
  below,
  splitPct,
  onSplitPct,
  fill = false,
}: {
  tracks: Track[];
  clock: PlaybackClock;
  /**
   * Rendered in the right-hand column, under the cameras. In practice the
   * question follower — what was on screen at this moment belongs beside the
   * footage of that moment, not below a timeline you have to scroll past.
   */
  aside?: React.ReactNode;
  /**
   * Rendered under the transport, inside the LEFT column only.
   *
   * The timeline goes here in review mode. Spanning it across the whole window
   * would cut the side column off partway down and waste the space under it —
   * the timeline belongs to the recording it scrubs, not to the page.
   */
  below?: React.ReactNode;
  /**
   * Percentage of the width the screen recording takes, when `fill` is on.
   * Omitted means the fixed four-fifths split of the normal layout.
   */
  splitPct?: number;
  onSplitPct?: (pct: number) => void;
  /**
   * Fill the height the parent gives, rather than sizing to the video's own
   * aspect ratio. Review mode uses this to give the screen recording the
   * window instead of a card.
   */
  fill?: boolean;
}) {
  // The screen recording is the densest feed and the only one nothing else can
  // stand in for, so it takes the width. The cameras only have to answer "is
  // this the same person, and where are they looking", which they do small.
  const screen = tracks.find((t) => t.source === "screen") ?? null;
  const cameras = tracks.filter((t) => t.source !== "screen");

  const audio = useAudioChoice(tracks);

  const controls = <PlaybackControls clock={clock} audio={audio} />;

  if (!screen) {
    // No screen recording (an older attempt). The cameras share the width and
    // the controls sit under them, as before.
    return (
      <div>
        <div className="grid grid-cols-1 divide-y divide-border border-b border-border sm:grid-cols-2 sm:divide-x sm:divide-y-0">
          {cameras.map((track) => (
            <TrackView
              key={track.source}
              track={track}
              clock={clock}
              muted={audio.mutedFor(track.source)}
              volume={audio.volume}
            />
          ))}
        </div>
        {controls}
        {below}
        {aside}
      </div>
    );
  }

  // Review mode lays out with flex and an explicit width so the divider can be
  // dragged. The normal page keeps the grid: there is nothing to drag there,
  // and a grid degrades to one column on a narrow screen for free.
  if (fill) {
    return (
      <div className="flex min-h-0 flex-1">
        <div
          className="flex min-w-0 flex-col"
          style={{ width: `${splitPct ?? 80}%` }}
        >
          <TrackView
            track={screen}
            clock={clock}
            muted={audio.mutedFor("screen")}
            volume={audio.volume}
            fill
          />
          <div className="shrink-0 border-t border-border">{controls}</div>
          {below ? (
            <div className="min-h-0 shrink-0 overflow-y-auto border-t border-border">
              {below}
            </div>
          ) : null}
        </div>

        <SplitHandle pct={splitPct ?? 80} onChange={onSplitPct} />

        <div className="flex min-h-0 min-w-0 flex-1 flex-col divide-y divide-border">
          {cameras.map((track) => (
            <div
              key={track.source}
              className="max-h-[20vh] shrink-0 overflow-hidden"
            >
              <TrackView
                track={track}
                clock={clock}
                muted={audio.mutedFor(track.source)}
                volume={audio.volume}
                compact
              />
            </div>
          ))}
          {aside ? (
            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
              {aside}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid grid-cols-1 divide-y divide-border lg:grid-cols-5 lg:divide-x lg:divide-y-0",
      )}
    >
      {/*
        Four fifths for the screen, with the transport DIRECTLY under it.
        The controls used to sit below the whole grid, which put them a long way
        from the picture they drive — and on a laptop they fell below the fold
        entirely, so the obvious way to pause was the browser's own context-menu
        controls, which drive one <video> and desync it from every other.
      */}
      <div className="flex min-w-0 flex-col lg:col-span-4">
        <TrackView
          track={screen}
          clock={clock}
          muted={audio.mutedFor("screen")}
          volume={audio.volume}
          fill={fill}
        />
        <div className="shrink-0 border-t border-border">{controls}</div>
        {below ? (
          <div className="min-h-0 shrink-0 overflow-y-auto border-t border-border">
            {below}
          </div>
        ) : null}
      </div>

      {/* One fifth: the cameras, then whatever the page puts beside them. */}
      <div
        className={cn(
          "flex min-w-0 flex-col divide-y divide-border lg:col-span-1",
          fill && "min-h-0",
        )}
      >
        {/* Capped in review mode. At 4:3 apiece two cameras eat the column and
            push the question panel off the bottom of the window — which is the
            one thing in here you read rather than glance at. */}
        {cameras.map((track) => (
          <div
            key={track.source}
            className={cn("shrink-0", fill && "max-h-[20vh] overflow-hidden")}
          >
            <TrackView
              track={track}
              clock={clock}
              muted={audio.mutedFor(track.source)}
              volume={audio.volume}
              compact
            />
          </div>
        ))}
        {aside ? (
          <div
            className={cn("min-w-0 flex-1", fill && "min-h-0 overflow-y-auto")}
          >
            {aside}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Mute, volume, and which recording the sound comes from.
 *
 * The transport had a speed control and nothing for sound at all, so an
 * attempt whose webcam recorded a whole hour of audio played silently with no
 * control to touch — indistinguishable from a microphone that never worked.
 */
function AudioControls({ audio }: { audio: AudioChoice }) {
  // Nothing was recorded at all; a volume slider over silence is a lie.
  if (audio.source === null) return null;

  const silent = audio.muted || audio.volume === 0;

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => {
          // Unmuting a slider sitting at zero has to restore a volume too,
          // or the button toggles and nothing happens — a dead control.
          if (silent && audio.volume === 0) audio.setVolume(1);
          audio.setMuted(!silent);
        }}
        aria-pressed={silent}
        aria-label={silent ? "Unmute the recording" : "Mute the recording"}
        title={silent ? "Unmute" : "Mute"}
        className={cn(
          "flex h-7 items-center gap-1 rounded-sm border px-1.5 text-2xs transition-colors",
          silent
            ? "border-border-strong bg-surface text-ink-faint hover:bg-surface-sunken"
            : "border-accent bg-accent-soft text-accent",
        )}
      >
        {silent ? (
          <VolumeX className="size-3.5" />
        ) : (
          <Volume2 className="size-3.5" />
        )}
      </button>

      <input
        type="range"
        min={0}
        max={100}
        value={Math.round(audio.volume * 100)}
        aria-label="Volume"
        onChange={(e) => {
          audio.setVolume(Number(e.target.value) / 100);
          // Dragging the slider up is an unmute — leaving it silent at 80%
          // reads as a broken control.
          if (audio.muted && Number(e.target.value) > 0) audio.setMuted(false);
        }}
        className="h-4 w-20 cursor-pointer accent-accent"
      />

      {/* Only worth showing when there is something to switch between. */}
      {audio.candidates.length > 1 ? (
        <select
          value={audio.source}
          aria-label="Which recording to listen to"
          onChange={(e) => audio.setSource(e.target.value as RecordingSource)}
          className="h-7 rounded-sm border border-border-strong bg-surface px-1 text-2xs text-ink-muted"
        >
          {audio.candidates.map((source) => (
            <option key={source} value={source}>
              {SOURCE_LABEL[source]}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}

/**
 * The draggable divider between the recording and the side panel.
 *
 * Clamped to 45-88%: below that the recording stops being readable, which is
 * the entire reason for review mode, and above it the side panel is too narrow
 * for a question stem. A divider that can be dragged into uselessness is a
 * divider someone will drag into uselessness mid-review and have to reload to
 * escape.
 */
function SplitHandle({
  pct,
  onChange,
}: {
  pct: number;
  onChange?: (pct: number) => void;
}) {
  const dragging = useRef(false);

  useEffect(() => {
    if (!onChange) return;

    // Bound to the WINDOW, not the handle: a fast drag outruns a 6px-wide
    // element, and losing the pointer mid-drag leaves the divider stuck.
    const move = (e: PointerEvent) => {
      if (!dragging.current) return;
      e.preventDefault();
      const next = (e.clientX / window.innerWidth) * 100;
      onChange(Math.min(88, Math.max(45, next)));
    };
    const up = () => {
      dragging.current = false;
      document.body.style.removeProperty("user-select");
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, [onChange]);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={Math.round(pct)}
      aria-label="Resize the recording"
      tabIndex={0}
      onPointerDown={() => {
        dragging.current = true;
        // Otherwise the drag selects every label it passes over.
        document.body.style.setProperty("user-select", "none");
      }}
      onKeyDown={(e) => {
        if (!onChange) return;
        if (e.key === "ArrowLeft") onChange(Math.max(45, pct - 2));
        if (e.key === "ArrowRight") onChange(Math.min(88, pct + 2));
      }}
      className="group w-1.5 shrink-0 cursor-col-resize bg-border transition-colors hover:bg-accent focus:bg-accent focus:outline-none"
      title="Drag to resize"
    />
  );
}

/** The transport. Split out so it can sit under the screen rather than the page. */
function PlaybackControls({
  clock,
  audio,
}: {
  clock: PlaybackClock;
  audio: AudioChoice;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2">
      <Button
        variant="primary"
        icon={clock.playing ? <Pause /> : <Play />}
        onClick={clock.toggle}
      >
        {clock.playing ? "Pause" : "Play"}
      </Button>
      <Button
        icon={<RotateCcw />}
        onClick={() => clock.nudge(-10_000)}
        aria-label="Back 10 seconds"
      >
        10s
      </Button>
      <Button
        icon={<RotateCw />}
        onClick={() => clock.nudge(10_000)}
        aria-label="Forward 10 seconds"
      >
        10s
      </Button>

      {clock.startMs < -999 ? (
        <Button
          icon={<SkipBack />}
          disabled={clock.timeMs === 0}
          onClick={() => clock.seek(0)}
        >
          Exam start
        </Button>
      ) : null}

      <span className="tabular ml-2 text-xs text-ink">
        {formatOffset(clock.timeMs)}
        <span className="text-ink-faint"> / {formatClock(clock.endMs)}</span>
      </span>
      {clock.timeMs < 0 ? (
        <span className="text-2xs text-ink-muted">
          before the exam started — setup, paper check, room scan
        </span>
      ) : null}

      <div className="ml-auto flex items-center gap-3">
        <AudioControls audio={audio} />

        <div className="flex items-center gap-1">
          <span className="text-2xs text-ink-faint">Speed</span>
          {RATES.map((rate) => (
            <button
              key={rate}
              type="button"
              onClick={() => clock.setRate(rate)}
              aria-pressed={clock.rate === rate}
              className={cn(
                "tabular h-7 rounded-sm border px-1.5 text-2xs transition-colors",
                clock.rate === rate
                  ? "border-accent bg-accent-soft text-accent"
                  : "border-border-strong bg-surface text-ink-muted hover:bg-surface-sunken",
              )}
            >
              {rate}×
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
